import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, MapPin, Calendar, DollarSign, FileText, Briefcase, Info, Home, Truck, Key } from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { locationService } from '@/services/locationService';
import { backendJobService } from '@/services/backendJobService';
import { LocationInput } from '@/components/location/LocationInput';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { StepProgress } from '@/components/ui/ProgressIndicator';
import { Tooltip } from '@/components/ui/Tooltip';
import { BuildingOfficeIcon } from '@heroicons/react/24/outline';

// Helper types
interface JobFormData {
  category: string;
  subcategory: string;
  title: string;
  description: string;
  urgency: 'today' | 'this-week' | 'this-month' | 'flexible';
  houseNumber: string;
  address: string;
  town: string; // City
  county: string;
  eirCode: string;
  propertyType: 'house' | 'apartment' | 'office' | 'other';
  accessInstructions: string;
  parkingAvailable: boolean;
  budgetMin: number;
  budgetMax: number;
  preferredDate: string;
  preferredTime: string;
  flexible: boolean;
  requirements: string[];
  photos: File[];
  additionalDetails: string;
  contactMethod: 'phone' | 'email' | 'both';
  providerCount: number;
  preferredProvider?: string;
}

const steps = [
  { number: 1, title: 'Service Details' },
  { number: 2, title: 'Location' },
  { number: 3, title: 'Budget & Timing' },
  { number: 4, title: 'Review' }
];

const urgencyOptions = [
  { value: 'today', label: 'Urgent (Today/Tomorrow)', description: 'I need this done immediately' },
  { value: 'this-week', label: 'This Week', description: 'Within the next 7 days' },
  { value: 'this-month', label: 'This Month', description: 'Flexible within 30 days' },
  { value: 'flexible', label: 'Flexible', description: 'Whenever available' }
];

export function CreateJobPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user } = useAuthStore();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [categories, setCategories] = useState<any[]>([]);
  const [subcategories, setSubcategories] = useState<any[]>([]);
  const [counties, setCounties] = useState<any[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [formData, setFormData] = useState<JobFormData>({
    category: '',
    subcategory: '',
    title: '',
    description: '',
    urgency: 'flexible',
    houseNumber: '',
    address: '',
    town: '',
    county: '',
    eirCode: '',
    propertyType: 'house',
    accessInstructions: '',
    parkingAvailable: true,
    budgetMin: 0,
    budgetMax: 0,
    preferredDate: '',
    preferredTime: '',
    flexible: true,
    requirements: [],
    photos: [],
    additionalDetails: '',
    contactMethod: 'both',
    providerCount: 3,
    preferredProvider: ''
  });

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      try {
        const [cats, countyData] = await Promise.all([
          backendJobService.getHomepageCategories(),
          locationService.getCounties()
        ]);
        setCategories(cats || []);
        setCounties(countyData || []);
      } catch (err) {
        console.error('Failed to load form data:', err);
      }
    };
    loadData();
  }, []);

  // Pre-fill from navigation state or sessionStorage draft
  useEffect(() => {
    const state = location.state as any;
    if (state?.from === 'login' && state.returnState) {
      setFormData(state.returnState);
      setCurrentStep(4); // Resume at review
      sessionStorage.removeItem('draft_job_form');
    } else if (!state?.prefilledCategory && !state?.prefilledCounty && !state?.category && !state?.service) {
      // Check sessionStorage draft (data survived a page refresh)
      const draft = sessionStorage.getItem('draft_job_form');
      if (draft) {
        try {
          setFormData(prev => ({ ...prev, ...JSON.parse(draft) }));
          setCurrentStep(4);
          sessionStorage.removeItem('draft_job_form');
        } catch {
          sessionStorage.removeItem('draft_job_form');
        }
      }
    }
    if (state?.prefilledCategory || state?.prefilledCounty) {
      // Pre-fill from home page search
      const updates: Partial<JobFormData> = {};
      
      // Pre-fill category
      if (state.prefilledCategory) {
        const selectedCat = categories.find(c => c.id === state.prefilledCategory);
        if (selectedCat) {
          updates.category = selectedCat.name;
        }
      }
      
      // Pre-fill location
      if (state.prefilledCounty) {
        updates.county = state.prefilledCounty;
      }
      if (state.prefilledAddress) {
        updates.address = state.prefilledAddress;
      }
      
      if (Object.keys(updates).length > 0) {
        setFormData(prev => ({ ...prev, ...updates }));
      }
    } else if (state?.category || state?.service) {
      setFormData(prev => ({
        ...prev,
        category: state.service || state.category || prev.category,
        subcategory: state.subcategory || prev.subcategory,
      }));
    }
  }, [location.state, categories]);

  // Load subcategories when category changes
  useEffect(() => {
    const loadSubcategories = async () => {
      if (!formData.category) {
        setSubcategories([]);
        return;
      }
      const selectedCat = categories.find(c => c.name.toLowerCase() === formData.category.toLowerCase());
      if (selectedCat) {
        try {
          const subs = await backendJobService.getCategorySubcategories(selectedCat.id);
          setSubcategories(subs);
        } catch (err) {
          console.error('Failed load subcategories', err);
        }
      }
    };
    loadSubcategories();
  }, [formData.category, categories]);

  const updateFormData = (updates: Partial<JobFormData>) => {
    setFormData(prev => ({ ...prev, ...updates }));
    setErrors({});
  };

  const handleLocationChange = (value: string, coordinates?: { lat: number; lng: number }, suggestion?: any) => {
    console.log('📍 Location selected:', { value, coordinates, suggestion });
    
    // Update address
    const updates: Partial<JobFormData> = { address: value };
    
    // If we have structured address data from HERE API, auto-populate fields
    if (suggestion?.structuredAddress) {
      const addr = suggestion.structuredAddress;
      
      if (addr.houseNumber) updates.houseNumber = addr.houseNumber;
      if (addr.city) updates.town = addr.city;
      if (addr.postalCode) updates.eirCode = addr.postalCode;
      
      // Match county - try to find exact or partial match
      if (addr.county) {
        const countyFromAPI = addr.county.toLowerCase().trim();
        
        // Try exact match first
        let matchedCounty = counties.find(c => 
          c.name.toLowerCase() === countyFromAPI
        );
        
        // If no exact match, try partial match (e.g., "County Dublin" -> "Dublin")
        if (!matchedCounty) {
          matchedCounty = counties.find(c => 
            countyFromAPI.includes(c.name.toLowerCase()) || 
            c.name.toLowerCase().includes(countyFromAPI)
          );
        }
        
        // If still no match, try removing "County" prefix
        if (!matchedCounty) {
          const cleanCounty = countyFromAPI.replace(/^county\s+/i, '');
          matchedCounty = counties.find(c => 
            c.name.toLowerCase() === cleanCounty
          );
        }
        
        if (matchedCounty) {
          updates.county = matchedCounty.name;
          console.log('✅ Matched county:', matchedCounty.name, 'from API value:', addr.county);
        } else {
          console.warn('⚠️ Could not match county:', addr.county, 'Available counties:', counties.map(c => c.name));
        }
      }
      
      console.log('✅ Auto-populated from structured address:', updates);
    }
    
    updateFormData(updates);
  };

  const validateStep = (step: number) => {
    const newErrors: Record<string, string> = {};
    if (step === 1) {
      if (!formData.category) newErrors.category = 'Service category is required';
      if (!formData.title || formData.title.length < 5) newErrors.title = 'Title must be at least 5 characters';
      if (!formData.description || formData.description.length < 10) newErrors.description = 'Description must be at least 10 characters';
    } else if (step === 2) {
      if (!formData.address) newErrors.address = 'Address is required';
      if (!formData.town) newErrors.town = 'Town/City is required';
      if (!formData.county) newErrors.county = 'County is required';
    } else if (step === 3) {
      if (formData.budgetMin < 0 || formData.budgetMax < 0 || (formData.budgetMax > 0 && formData.budgetMin > formData.budgetMax)) {
        newErrors.budget = 'Invalid budget range';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handeNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
      window.scrollTo(0, 0);
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    window.scrollTo(0, 0);
  };

  const handleSubmit = async () => {
    if (!isAuthenticated) {
      // Save to sessionStorage as backup in case page refreshes during login
      const { photos: _photos, ...serializableData } = formData;
      sessionStorage.setItem('draft_job_form', JSON.stringify(serializableData));
      navigate('/login', { state: { returnTo: '/create-job', returnState: formData } });
      return;
    }

    setIsSubmitting(true);
    try {
      const selectedCat = categories.find(c => c.name.toLowerCase() === formData.category.toLowerCase());

      const jobPayload = {
        title: formData.title,
        description: formData.description,
        category_id: selectedCat?.id,
        subcategory: formData.subcategory,
        location: `${formData.address}, ${formData.town}, ${formData.county}`,
        county: formData.county, // Add county for provider matching
        priority: formData.urgency === 'today' ? 'high' : 'medium',
        budget_min: formData.budgetMin,
        budget_max: formData.budgetMax,
        preferred_date: formData.preferredDate,
        // Map other fields as needed by your backend
      };

      await backendJobService.createJob(jobPayload);
      sessionStorage.removeItem('draft_job_form');
      navigate('/customer-jobs', { state: { message: 'Job posted successfully!' } });
    } catch (err) {
      console.error('Submission failed', err);
      setErrors({ submit: 'Failed to create job. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 pb-20">
      {/* Skip Links */}
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary-600 focus:text-white focus:rounded-lg focus:shadow-lg"
      >
        Skip to main content
      </a>
      
      <div className="max-w-3xl mx-auto px-4 py-6 sm:py-8">
        <Breadcrumb items={[{ label: 'Post a Job' }]} />

        <div className="page-header mt-4 text-center">
          <h1 className="page-title">Post a New Job</h1>
          <p className="page-description px-4">Get quotes from qualified professionals in minutes.</p>
        </div>

        {/* Progress Steps */}
        <div className="mb-6 sm:mb-8" id="main-content">
          <StepProgress
            steps={steps.map(s => ({
              id: s.number.toString(),
              label: s.title,
            }))}
            currentStep={currentStep - 1}
            variant="horizontal"
          />
        </div>

        <Card className="p-0 overflow-hidden">
          {/* Step 1: Services */}
          {currentStep === 1 && (
            <div className="p-6 space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Service Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => updateFormData({ category: e.target.value, subcategory: '' })}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:outline-none"
                >
                  <option value="">Select a category</option>
                  {categories.map(c => (
                    <option key={c.id || c.name} value={c.name}>{c.name}</option>
                  ))}
                </select>
                {errors.category && <p className="text-red-500 text-sm mt-1">{errors.category}</p>}
              </div>

              {subcategories.length > 0 && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Specific Service</label>
                  <select
                    value={formData.subcategory}
                    onChange={(e) => updateFormData({ subcategory: e.target.value })}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:outline-none"
                  >
                    <option value="">Select a service</option>
                    {subcategories.map(s => (
                      <option key={s.id || s.name} value={s.name}>{s.name}</option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Job Title</label>
                <input
                  type="text"
                  placeholder="e.g. Fix a leaking tap"
                  value={formData.title}
                  onChange={(e) => updateFormData({ title: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:outline-none"
                />
                {errors.title && <p className="text-red-500 text-sm mt-1">{errors.title}</p>}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
                <textarea
                  rows={4}
                  placeholder="Describe the issue in detail..."
                  value={formData.description}
                  onChange={(e) => updateFormData({ description: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:outline-none"
                />
                {errors.description && <p className="text-red-500 text-sm mt-1">{errors.description}</p>}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Urgency</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {urgencyOptions.map(opt => (
                    <div
                      key={opt.value}
                      onClick={() => updateFormData({ urgency: opt.value as any })}
                      role="radio"
                      aria-checked={formData.urgency === opt.value}
                      tabIndex={0}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          updateFormData({ urgency: opt.value as any });
                        }
                      }}
                      className={`cursor-pointer p-4 rounded-lg border text-left transition-all min-h-touch ${formData.urgency === opt.value
                          ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-500'
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                        }`}
                    >
                      <div className="font-semibold text-sm text-gray-900">{opt.label}</div>
                      <div className="text-xs text-gray-500 mt-1">{opt.description}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Location */}
          {currentStep === 2 && (
            <div className="p-6 space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <div className="flex items-start gap-2">
                  <MapPin className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">Start typing your address</p>
                    <p className="text-xs">We'll automatically fill in your town, county, and Eircode when you select an address from the dropdown.</p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Search Address <span className="text-red-500">*</span>
                </label>
                <LocationInput
                  value={formData.address}
                  onChange={handleLocationChange}
                  placeholder="Start typing your address (e.g., 123 Main Street, Dublin)..."
                  showSavedAddresses={true}
                  showCurrentLocation={true}
                />
                {errors.address && <p className="text-red-500 text-sm mt-1">{errors.address}</p>}
                <p className="text-xs text-gray-500 mt-1">
                  💡 Tip: Include house number and street name for best results
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    House Number
                  </label>
                  <input
                    type="text"
                    value={formData.houseNumber}
                    onChange={(e) => updateFormData({ houseNumber: e.target.value })}
                    placeholder="e.g., 123"
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:outline-none"
                  />
                  <p className="text-xs text-gray-500 mt-1">Auto-filled from address</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Eircode
                  </label>
                  <input
                    type="text"
                    value={formData.eirCode}
                    onChange={(e) => updateFormData({ eirCode: e.target.value.toUpperCase() })}
                    placeholder="e.g., D02 XY45"
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:outline-none uppercase"
                  />
                  <p className="text-xs text-gray-500 mt-1">Auto-filled from address</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Town / City <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.town}
                    onChange={(e) => updateFormData({ town: e.target.value })}
                    placeholder="e.g., Dublin"
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:outline-none"
                  />
                  {errors.town && <p className="text-red-500 text-sm mt-1">{errors.town}</p>}
                  <p className="text-xs text-gray-500 mt-1">Auto-filled from address</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    County <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.county}
                    onChange={(e) => updateFormData({ county: e.target.value })}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:outline-none"
                  >
                    <option value="">Select County</option>
                    {counties.map(c => (
                      <option key={c.id || c.name} value={c.name}>{c.name}</option>
                    ))}
                  </select>
                  {errors.county && <p className="text-red-500 text-sm mt-1">{errors.county}</p>}
                  <p className="text-xs text-gray-500 mt-1">Auto-filled from address</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Property Type</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {['house', 'apartment', 'office'].map(type => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => updateFormData({ propertyType: type as any })}
                      className={`py-3 px-4 rounded-lg border font-medium text-sm capitalize transition-all min-h-touch ${formData.propertyType === type
                          ? 'bg-primary-50 border-primary-500 text-primary-700 ring-2 ring-primary-200'
                          : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300'
                        }`}
                      aria-pressed={formData.propertyType === type}
                    >
                      {type === 'house' && <Home className="w-5 h-5 mx-auto mb-1" />}
                      {type === 'apartment' && <Briefcase className="w-5 h-5 mx-auto mb-1" />}
                      {type === 'office' && <BuildingOfficeIcon className="w-5 h-5 mx-auto mb-1" />}
                      <div>{type}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Access Instructions (Optional)
                </label>
                <textarea
                  rows={2}
                  value={formData.accessInstructions}
                  onChange={(e) => updateFormData({ accessInstructions: e.target.value })}
                  placeholder="e.g., Gate code is 1234, park on the street..."
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:outline-none"
                />
                <p className="text-xs text-gray-500 mt-1">Help providers find and access your property</p>
              </div>
            </div>
          )}

          {/* Step 3: Budget */}
          {currentStep === 3 && (
            <div className="p-6 space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Estimated Budget (Optional)</label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">€</span>
                    <input
                      type="number"
                      placeholder="Min"
                      value={formData.budgetMin || ''}
                      onChange={(e) => updateFormData({ budgetMin: parseInt(e.target.value) || 0 })}
                      className="w-full pl-8 p-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">€</span>
                    <input
                      type="number"
                      placeholder="Max"
                      value={formData.budgetMax || ''}
                      onChange={(e) => updateFormData({ budgetMax: parseInt(e.target.value) || 0 })}
                      className="w-full pl-8 p-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Preferred Date</label>
                <input
                  type="date"
                  value={formData.preferredDate}
                  onChange={(e) => updateFormData({ preferredDate: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>
          )}

          {/* Step 4: Review */}
          {currentStep === 4 && (
            <div className="p-6 space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-100 space-y-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-semibold text-gray-900">{formData.title}</h3>
                    <p className="text-sm text-gray-500 mt-1">{formData.category} • {formData.propertyType}</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setCurrentStep(1)}>Edit</Button>
                </div>
                <p className="text-sm text-gray-700 bg-white p-3 rounded border border-gray-200">
                  {formData.description}
                </p>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  {formData.address}, {formData.town}
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  {formData.preferredDate ? new Date(formData.preferredDate).toLocaleDateString() : 'Flexible Date'}
                </div>
              </div>

              <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-100 rounded-lg">
                <Tooltip content="Your contact details will only be shared with the professional you choose to hire. We never share your information with third parties.">
                  <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                </Tooltip>
                <p className="text-sm text-blue-800">
                  By posting this job, you agree to receive quotes from verified professionals. Your contact details will only be shared with the professional you hire.
                </p>
              </div>
            </div>
          )}

          {/* Footer Navigation */}
          <div className="p-4 sm:p-6 bg-gray-50 border-t border-gray-200 flex flex-col-reverse sm:flex-row justify-between items-stretch sm:items-center gap-3">
            <Button
              variant="ghost"
              onClick={handleBack}
              disabled={currentStep === 1}
              className="w-full sm:w-auto"
              aria-label="Go to previous step"
            >
              <ArrowLeft className="w-4 h-4 mr-2" aria-hidden="true" /> Back
            </Button>

            {currentStep < 4 ? (
              <Button 
                onClick={handeNext} 
                variant="primary"
                className="w-full sm:w-auto"
                aria-label="Go to next step"
              >
                Next Step <ArrowRight className="w-4 h-4 ml-2" aria-hidden="true" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                variant="primary"
                isLoading={isSubmitting}
                disabled={isSubmitting}
                className="w-full sm:w-auto"
                aria-label="Submit job posting"
              >
                Post Job Now
              </Button>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}